// Session Tracker Model - for tracking individual sets during a workout session

export class SetRecord {
  constructor(data = {}) {
    this.id = data.id || generateId();
    this.setNumber = data.setNumber || 1;
    this.reps = data.reps || 0;
    this.weight = data.weight ?? null;
    this.completed = data.completed || false;
    this.notes = data.notes || '';
  }

  complete() {
    this.completed = true;
  }

  reset() {
    this.completed = false;
  }
}

export class ExerciseSession {
  constructor(data = {}) {
    this.id = data.id || generateId();
    this.exerciseName = data.exerciseName || '';
    this.targetSets = data.targetSets || 3;
    this.targetReps = data.targetReps || 10;
    this.defaultWeight = data.defaultWeight ?? null;
    this.sets = data.sets || this.initializeSets(data.targetSets, data.targetReps, data.defaultWeight);
  }

  initializeSets(targetSets, targetReps, defaultWeight) {
    return Array.from({ length: targetSets }, (_, i) => 
      new SetRecord({
        setNumber: i + 1,
        reps: targetReps,
        weight: defaultWeight
      })
    );
  }

  addSet() {
    const newSet = new SetRecord({
      setNumber: this.sets.length + 1,
      reps: this.targetReps,
      weight: this.defaultWeight
    });
    this.sets.push(newSet);
    return newSet;
  }

  deleteSet(setId) {
    this.sets = this.sets.filter(s => s.id !== setId);
    // Renumber remaining sets
    this.sets.forEach((set, idx) => {
      set.setNumber = idx + 1;
    });
  }

  completeSet(setId) {
    const set = this.sets.find(s => s.id === setId);
    if (set) {
      set.complete();
    }
  }

  getProgress() {
    const completed = this.sets.filter(s => s.completed).length;
    return {
      completed,
      total: this.sets.length,
      percentage: this.sets.length > 0 ? (completed / this.sets.length) * 100 : 0
    };
  }

  isComplete() {
    return this.sets.length > 0 && this.sets.every(s => s.completed);
  }
}

export class WorkoutSession {
  constructor(data = {}) {
    this.id = data.id || generateId();
    this.name = data.name || '';
    this.date = data.date || new Date().toISOString();
    this.exercises = data.exercises || [];
    this.completed = data.completed || false;
    this.completedAt = data.completedAt || null;
    this.rewards = data.rewards || { xp: 0 };
  }

  addExercise(exerciseSession) {
    this.exercises.push(exerciseSession);
  }

  removeExercise(exerciseId) {
    this.exercises = this.exercises.filter(e => e.id !== exerciseId);
  }

  copyExercise(exerciseId) {
    const exercise = this.exercises.find(e => e.id === exerciseId);
    if (exercise) {
      const copied = new ExerciseSession({
        exerciseName: exercise.exerciseName,
        targetSets: exercise.targetSets,
        targetReps: exercise.targetReps,
        defaultWeight: exercise.defaultWeight
      });
      this.exercises.push(copied);
      return copied;
    }
    return null;
  }

  getProgress() {
    if (this.exercises.length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    const completedExercises = this.exercises.filter(e => e.isComplete()).length;
    return {
      completed: completedExercises,
      total: this.exercises.length,
      percentage: (completedExercises / this.exercises.length) * 100
    };
  }

  isComplete() {
    return this.exercises.length > 0 && this.exercises.every(e => e.isComplete());
  }

  complete(xpReward = 0) {
    this.completed = true;
    this.completedAt = new Date().toISOString();
    this.rewards.xp = xpReward;
    return this.rewards;
  }
}

function generateId() {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
